const toId = value => String(value);

export function normalizePricePoint(pricePoint) {
  const rawSortOrder = pricePoint.sort_order;
  const sortOrder = rawSortOrder === undefined || rawSortOrder === null
    ? NaN
    : Number(rawSortOrder);
  return Object.assign({}, pricePoint, {
    is_pinned: pricePoint.is_pinned === undefined || pricePoint.is_pinned === null
      ? true
      : pricePoint.is_pinned === true || Number(pricePoint.is_pinned) === 1,
    pin_priority: Math.max(0, Number(pricePoint.pin_priority) || 0),
    sort_order: Number.isFinite(sortOrder) ? sortOrder : (Number(pricePoint.id) || 0),
  });
}

function compare(a, b) {
  if (a.is_pinned !== b.is_pinned) return a.is_pinned ? -1 : 1;
  if (a.is_pinned && a.pin_priority !== b.pin_priority) return b.pin_priority - a.pin_priority;
  if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
  return (Number(a.id) || 0) - (Number(b.id) || 0);
}

export function orderedPricePoints(list) {
  const items = Array.isArray(list) ? list : [];
  return items
    .map((pricePoint, index) => ({ pricePoint, index, norm: normalizePricePoint(pricePoint) }))
    .sort((a, b) => compare(a.norm, b.norm) || a.index - b.index)
    .map(entry => entry.pricePoint);
}

export function groupPricePoints(list) {
  const items = Array.isArray(list) ? list : [];
  const pinned = [];
  const more = [];
  orderedPricePoints(items).forEach((pricePoint) => {
    (isInMainList(pricePoint) ? pinned : more).push({ pricePoint, index: items.indexOf(pricePoint) });
  });
  return { pinned, more };
}

export function buildOrderPayload(orderedPinned, orderedMore) {
  const main = orderedPinned || [];
  const more = orderedMore || [];
  const all = main.concat(more);
  const pins = main.filter(isPricePointPinned);
  const normal = main.filter(p => !isPricePointPinned(p)).concat(more);
  // Reserve pinned items' original slots so unpinning still restores their normal position.
  const baseline = all.slice().sort((a, b) => normalizePricePoint(a).sort_order - normalizePricePoint(b).sort_order);
  const positions = new Map();
  baseline.forEach((point, index) => {
    const item = pins.includes(point) ? point : normal.shift();
    positions.set(toId(item.id), index);
  });
  return { items: all.map(point => ({
    id: point.id,
    is_pinned: main.includes(point),
    pin_priority: pins.includes(point) ? pins.length - pins.indexOf(point) : 0,
    sort_order: positions.get(toId(point.id)),
  })) };
}

// is_pinned predates independent pins and remains the API's main-list visibility flag.
export function isInMainList(point) {
  return normalizePricePoint(point).is_pinned;
}

export function isPricePointPinned(point) {
  const normalized = normalizePricePoint(point);
  return normalized.is_pinned && normalized.pin_priority > 0;
}

export function buildTogglePayload(list, id, action) {
  const items = (list || []).map(point => {
    const normalized = normalizePricePoint(point);
    return { id: point.id, is_pinned: normalized.is_pinned, pin_priority: normalized.pin_priority, sort_order: normalized.sort_order };
  });
  const point = items.find(item => toId(item.id) === toId(id));
  if (!point) return { items };
  if (action === 'pin') {
    point.is_pinned = true;
    point.pin_priority = Math.max(0, ...items.map(item => item.pin_priority)) + 1;
  } else if (action === 'unpin') {
    point.pin_priority = 0;
  } else if (action === 'hide') {
    point.is_pinned = false;
    point.pin_priority = 0;
  } else if (action === 'show') {
    point.is_pinned = true;
  }
  return { items };
}

export function reorderByIds(array, ids) {
  const items = Array.isArray(array) ? array : [];
  const wanted = (ids || []).map(toId);
  const byId = new Map(items.map(item => [toId(item.id), item]));
  const picked = wanted.map(id => byId.get(id)).filter(Boolean);
  const pickedSet = new Set(picked);
  return picked.concat(items.filter(item => !pickedSet.has(item)));
}

/**
 * Price points a checkout link applies to. Links without explicit options apply
 * to every price point, matching the checkout-link metadata shown in the wizard.
 */
export function pricePointsForCheckoutLink(pricePoints, checkoutLink) {
  const points = orderedPricePoints(pricePoints);
  const options = checkoutLink && Array.isArray(checkoutLink.pricing_options)
    ? checkoutLink.pricing_options.filter((option) => option.enabled !== false)
    : [];
  const ids = options.map((option) => toId(option.id || option.price_point_id));
  return ids.length ? points.filter((point) => ids.includes(toId(point.id))) : points;
}

/**
 * Order checkout links the way the Pricing step lists price points, and split
 * out links that only reference hidden ("More price points") price points.
 */
export function groupCheckoutLinksByPricePoints(checkoutLinks, pricePoints) {
  const points = orderedPricePoints(pricePoints);
  const rank = new Map();
  points.forEach((point, index) => rank.set(toId(point.id), index));

  const entries = (Array.isArray(checkoutLinks) ? checkoutLinks : []).map((link, index) => {
    const matched = pricePointsForCheckoutLink(points, link);
    const positions = matched
      .map((point) => rank.get(toId(point.id)))
      .filter((value) => value !== undefined);

    return {
      link,
      index,
      order: positions.length ? Math.min(...positions) : Number.MAX_SAFE_INTEGER,
      hidden: matched.length > 0 && matched.every((point) => !isInMainList(point)),
    };
  });

  entries.sort((a, b) => (a.order - b.order) || (a.index - b.index));

  return {
    primary: entries.filter((entry) => !entry.hidden).map((entry) => entry.link),
    hidden: entries.filter((entry) => entry.hidden).map((entry) => entry.link),
  };
}
